// apps/frontend/src/components/Referrals/ReferralsPage.jsx

import React, { useState, useEffect } from "react";
import { LangContext } from "../../contexts/LangContext";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const API_BASE = "https://flautreach.com/flautreach/api";

export function ReferralsPage() {
  const { lang } = React.useContext(LangContext);
  const t = lang === "pl" ? pl : en;

  // ============ TRANSLATION BLOCKS ============
  const txt = {
    // Loading State
    loading: t.referralsPage.loading,
    
    // Header
    headerTitle: t.referralsPage.headerTitle,
    headerSubtitle: t.referralsPage.headerSubtitle,
    
    // Referral Link Card
    linkCardTitle: t.referralsPage.linkCardTitle,
    linkCardDescription: t.referralsPage.linkCardDescription,
    
    // Referral Info
    referralId: t.referralsPage.referralId,
    created: t.referralsPage.created,
    copy: t.referralsPage.copy,
    copied: t.referralsPage.copied,
    
    // Buttons
    createButton: t.referralsPage.createButton,
    creating: t.referralsPage.creating,
    
    // Summary Cards
    totalReferrals: t.referralsPage.totalReferrals,
    active: t.referralsPage.active,
    totalEarned: t.referralsPage.totalEarned,
    fromReferralsPayments: t.referralsPage.fromReferralsPayments,
    totalPayments: t.referralsPage.totalPayments,
    successfulPayments: t.referralsPage.successfulPayments,
    
    // Table
    tableTitle: t.referralsPage.tableTitle,
    tableEmpty: t.referralsPage.tableEmpty,
    
    // Table Columns
    userColumn: t.referralsPage.userColumn,
    registeredColumn: t.referralsPage.registeredColumn,
    paymentsColumn: t.referralsPage.paymentsColumn,
    earnedColumn: t.referralsPage.earnedColumn,
    
    // Mobile Labels
    userLabel: t.referralsPage.userLabel,
    registeredLabel: t.referralsPage.registeredLabel,
    paymentsLabel: t.referralsPage.paymentsLabel,
    earnedLabel: t.referralsPage.earnedLabel,
    
    // Pagination
    previous: t.referralsPage.previous,
    next: t.referralsPage.next,
    pageOf: t.referralsPage.pageOf,
    
    // Error Messages
    failedToLoad: t.referralsPage.failedToLoad,
    failedToCreate: t.referralsPage.failedToCreate,
    failedToDelete: t.referralsPage.failedToDelete,
    deleteConfirmation: t.referralsPage.deleteConfirmation,
    errorDefault: t.referralsPage.errorDefault,
    
    // Formatting
    dateLocale: t.referralsPage.dateLocale,
    currency: t.referralsPage.currency,
  };

  // ============ STATE ============
  const [referralLink, setReferralLink] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  
  // Stats pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // ============ LOAD DATA ============
  const loadData = async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = getCookie("authToken");
      
      const [linksResponse, statsResponse] = await Promise.all([
        fetch(`${API_BASE}/referrals/`, {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/referrals/stats/?page=${page}&page_size=${pageSize}`, {
          headers: { "Authorization": `Bearer ${token}` }
        })
      ]);
      
      if (!linksResponse.ok || !statsResponse.ok) {
        throw new Error(txt.errorDefault);
      }
      
      const linksData = await linksResponse.json();
      const statsData = await statsResponse.json();
      
      setReferralLink(linksData[0] || null);
      setStats(statsData);
    } catch (err) {
      setError(err.message || txt.failedToLoad);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page]);

  // ============ HANDLERS ============
  const handleCreateLink = async () => {
    setCreating(true);
    setError("");
    
    try {
      const token = getCookie("authToken");
      const response = await fetch(`${API_BASE}/referrals/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || txt.failedToCreate);
      }
      
      const data = await response.json();
      setReferralLink(data);
    } catch (err) {
      setError(err.message || txt.failedToCreate);
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = () => {
    if (!referralLink?.referral_url) return;
    
    navigator.clipboard.writeText(referralLink.referral_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDeleteLink = async () => {
    if (!referralLink?.id) return;
    if (!window.confirm(txt.deleteConfirmation)) return;
    
    try {
      const token = getCookie("authToken");
      const response = await fetch(`${API_BASE}/referrals/${referralLink.id}/`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error(txt.failedToDelete);
      
      setReferralLink(null);
    } catch (err) {
      setError(err.message || txt.failedToDelete);
    }
  };

  // ============ FORMATTERS ============
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString(txt.dateLocale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatMoney = (val) => {
    const num = parseFloat(val) || 0;
    return num.toFixed(2);
  };

  // ============ PAGE OF FORMATTER ============
  const formatPageOf = (current, total) => {
    return txt.pageOf.replace('{page}', current).replace('{totalPages}', total);
  };

  // ============ LOADING STATE ============
  if (loading && !stats) {
    return (
      <div className="add-rule-wrap" style={{ maxWidth: 900 }}>
        <div style={{ textAlign: "center", padding: 60 }}>
          <div className="spinner" />
          <p style={{ marginTop: 16, color: "var(--muted)", fontSize: 13 }}>{txt.loading}</p>
        </div>
      </div>
    );
  }

  // ============ RENDER ============
  return (
    <div className="add-rule-wrap" style={{ maxWidth: 900 }}>
      
      {/* HEADER */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{txt.headerTitle}</h2>
        <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: 13 }}>
          {txt.headerSubtitle}
        </p>
      </div>

      {/* REFERRAL LINK CARD */}
      <div className="section-card" style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{txt.linkCardTitle}</h3>
        <p style={{ fontSize: 14, color: "var(--muted)", margin: "8px 0 16px" }}>
          {txt.linkCardDescription}
        </p>

        {error && (
          <div style={{
            padding: 10,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 12,
            color: "#991b1b"
          }}>
            {error}
          </div>
        )}

        {referralLink ? (
          <div>
            {/* Link display */}
            <div className="referral-link-container" style={{
              marginBottom: 16,
              padding: 12,
              background: "#f8fafc",
              borderRadius: 8,
              border: "1px solid var(--border)",
            }}>
              <div style={{
                flex: 1,
                minWidth: 0,
                fontSize: 14,
                fontFamily: "monospace",
                color: "var(--dark)",
                padding: "8px 0",
                wordBreak: "break-all",
                lineHeight: 1.5
              }}>
                {referralLink.referral_url}
              </div>
              <button
                onClick={handleCopyLink}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "none",
                  background: copied ? "#166534" : "var(--dark)",
                  color: "#fff",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s",
                  flexShrink: 0,
                  alignSelf: "flex-start"
                }}
              >
                {copied ? txt.copied : txt.copy}
              </button>
            </div>

            {/* Link info */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
              <div style={{ padding: 12, background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd" }}>
                <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 4, fontWeight: 600, textTransform: "uppercase" }}>{txt.referralId}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0369a1", fontFamily: "monospace" }}>{referralLink.referral_id}</div>
              </div>
              <div style={{ padding: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 4, fontWeight: 600, textTransform: "uppercase" }}>{txt.created}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--dark)" }}>{formatDate(referralLink.created_at)}</div>
              </div>
            </div>

           
          </div>
        ) : (
          <button
            onClick={handleCreateLink}
            disabled={creating}
            className="btn-primary"
            style={{
              width: "100%",
              padding: 12,
              fontSize: 14,
              fontWeight: 700,
              opacity: creating ? 0.7 : 1,
            }}
          >
            {creating ? txt.creating : txt.createButton}
          </button>
        )}
      </div>

      {/* STATISTICS */}
      {stats && (
        <>
          {/* Summary Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 24 }}>
            <div className="section-card" style={{ marginBottom: 0 }}>
              <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8, fontWeight: 600 }}>{txt.totalReferrals}</div>
              <div style={{ fontSize: "clamp(24px, 5vw, 32px)", fontWeight: 800, color: "var(--dark)" }}>
                {stats.summary.total_referrals}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                {stats.summary.active_referrals} {txt.active}
              </div>
            </div>
            
            <div className="section-card" style={{ marginBottom: 0 }}>
              <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8, fontWeight: 600 }}>{txt.totalEarned}</div>
              <div style={{ fontSize: "clamp(24px, 5vw, 32px)", fontWeight: 800, color: "#166534" }}>
                ${formatMoney(stats.summary.total_payments_sum)}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                {txt.fromReferralsPayments}
              </div>
            </div>
            
            <div className="section-card" style={{ marginBottom: 0 }}>
              <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8, fontWeight: 600 }}>{txt.totalPayments}</div>
              <div style={{ fontSize: "clamp(24px, 5vw, 32px)", fontWeight: 800, color: "var(--dark)" }}>
                {stats.summary.total_payments_count}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                {txt.successfulPayments}
              </div>
            </div>
          </div>

          {/* Referrals Table */}
          <div className="section-card">
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>{txt.tableTitle}</h3>
            
            {stats.results.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--muted)", fontSize: 13 }}>
                {txt.tableEmpty}
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="referrals-table-desktop" style={{ display: "block" }}>
                  <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{txt.userColumn}</th>
                        <th style={thStyle}>{txt.registeredColumn}</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>{txt.paymentsColumn}</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>{txt.earnedColumn}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.results.map((ref, i) => (
                        <tr key={ref.user_id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 600 }}>{ref.user_email}</div>
                          </td>
                          <td style={tdStyle}>
                            {formatDate(ref.registration_date)}
                          </td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>
                            <span style={{
                              padding: "2px 8px",
                              borderRadius: 10,
                              fontSize: 12,
                              fontWeight: 600,
                              background: ref.payments_count > 0 ? "#f0fdf4" : "#f8fafc",
                              color: ref.payments_count > 0 ? "#166534" : "var(--muted)",
                            }}>
                              {ref.payments_count}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: ref.total_payments > 0 ? "#166534" : "var(--muted)" }}>
                            ${formatMoney(ref.total_payments)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="referrals-table-mobile">
                  {stats.results.map((ref, i) => (
                    <div key={ref.user_id} style={{
                      padding: 12,
                      marginBottom: 8,
                      background: "#f8fafc",
                      borderRadius: 8,
                      border: "1px solid var(--border)"
                    }}>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>{txt.userLabel}</div>
                        <div style={{ fontWeight: 600, fontSize: 14, wordBreak: "break-all" }}>{ref.user_email}</div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 2 }}>{txt.registeredLabel}</div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{formatDate(ref.registration_date)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 2 }}>{txt.paymentsLabel}</div>
                          <span style={{
                            padding: "2px 8px",
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 600,
                            background: ref.payments_count > 0 ? "#f0fdf4" : "#f8fafc",
                            color: ref.payments_count > 0 ? "#166534" : "var(--muted)",
                          }}>
                            {ref.payments_count}
                          </span>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 2 }}>{txt.earnedLabel}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: ref.total_payments > 0 ? "#166534" : "var(--muted)" }}>
                            ${formatMoney(ref.total_payments)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {stats.pagination.total_pages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={!stats.pagination.has_previous}
                      style={paginationBtnStyle}
                    >
                      {txt.previous}
                    </button>
                    
                    <span style={{ fontSize: 13, color: "var(--muted)", fontWeight: 600 }}>
                      {formatPageOf(stats.pagination.page, stats.pagination.total_pages)}
                    </span>
                    
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={!stats.pagination.has_next}
                      style={paginationBtnStyle}
                    >
                      {txt.next}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Styles */}
      <style>{`
        .referral-link-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        @media (min-width: 641px) {
          .referral-link-container {
            flex-direction: row;
            align-items: flex-start;
            gap: 12px;
          }
        }
        
        .referrals-table-desktop {
          display: block;
        }
        .referrals-table-mobile {
          display: none;
        }
        
        @media (max-width: 640px) {
          .referrals-table-desktop {
            display: none !important;
          }
          .referrals-table-mobile {
            display: block !important;
          }
          
          .add-rule-wrap {
            padding: 12px !important;
          }
          
          .section-card {
            padding: 12px !important;
          }
          
          table.table {
            font-size: 11px;
          }
          
          th, td {
            padding: 8px 6px !important;
          }
        }
      `}</style>
    </div>
  );
}

// ============ STYLES ============
const thStyle = {
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 700,
  color: "var(--muted)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  borderBottom: "2px solid var(--border)",
  textAlign: "left",
  background: "#fafafa",
};

const tdStyle = {
  padding: "12px",
  fontSize: 13,
  color: "var(--dark)",
  verticalAlign: "middle",
};

const paginationBtnStyle = {
  padding: "8px 16px",
  borderRadius: 6,
  border: "1px solid var(--border)",
  background: "#fff",
  color: "var(--dark)",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  opacity: 1,
};